import React, { useState } from 'react';
import { RoomInfo } from '@shared/types';
import socket from '@/socket';

interface LobbyProps {
  room: RoomInfo;
  mySessionKey: string;
  onLeave: () => void;
}

const AVATAR_MAP: Record<string, string> = {
  fox: '🦊', tiger: '🐯', lion: '🦁', bear: '🐻',
  panda: '🐼', koala: '🐨', raccoon: '🦝', owl: '🦉',
};

export const Lobby: React.FC<LobbyProps> = ({ room, mySessionKey, onLeave }) => {
  const [isReady, setIsReady] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const myPlayer = room.players.find(p => p.seat === room.hostSeat);
  const amHost = !!myPlayer;

  const handleReady = () => {
    const next = !isReady;
    setIsReady(next);
    socket.emit('lobby:setReady', { ready: next });
  };

  const handleStart = () => {
    setStarting(true);
    setError('');
    socket.emit('lobby:startGame', {}, (res) => {
      setStarting(false);
      if (!res.success) setError(res.error ?? 'Başlatılamadı');
    });
  };

  const copyCode = () => {
    navigator.clipboard.writeText(room.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const allReady = room.players.length === 4 && room.players.every(p => p.isReady);
  const seats = [0, 1, 2, 3] as const;

  return (
    <div className="lobby-screen">
      <div className="lobby-card">
        {/* Header */}
        <div className="lobby-header">
          <div className="lobby-title">
            {room.mode === '101' ? '101 Okey' : 'Normal Okey'} — Bekleme Odası
          </div>
          <div className="lobby-code">
            <span className="lobby-code-label">KOD:</span>
            <span className="lobby-code-value">{room.code}</span>
            <button className="copy-btn" onClick={copyCode} title="Kopyala">
              {copied ? '✓' : '📋'}
            </button>
          </div>
        </div>

        {/* Player info */}
        <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
          {room.players.length}/4 oyuncu — Arkadaşlarına kodu ver
        </div>

        {/* Seats */}
        <div className="lobby-seats">
          {seats.map(seat => {
            const player = room.players.find(p => p.seat === seat);
            return (
              <div
                key={seat}
                className={[
                  'lobby-seat',
                  player ? (player.isReady ? 'ready' : 'filled') : 'empty',
                  player?.seat === room.hostSeat ? 'mine' : '',
                ].filter(Boolean).join(' ')}
              >
                <div className="lobby-seat-avatar">
                  {player ? (AVATAR_MAP[player.avatar] ?? '🦊') : '·'}
                </div>
                <div className="lobby-seat-info">
                  <div className="lobby-seat-name">
                    {player ? player.nickname : 'Boş'}
                  </div>
                  <div className={`lobby-seat-status ${player ? (player.isReady ? 'ready' : 'waiting') : 'empty'}`}>
                    {player ? (player.isReady ? '✓ Hazır' : 'Bekleniyor...') : 'Oyuncu bekleniyor'}
                  </div>
                </div>
                <div className="lobby-seat-num">{seat + 1}</div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="lobby-actions">
          {error && (
            <div style={{ fontSize: 12, color: 'var(--color-danger)', textAlign: 'center' }}>
              {error}
            </div>
          )}

          <button
            className={`btn btn-lg ${isReady ? 'btn-ghost' : 'btn-green'}`}
            style={{ width: '100%' }}
            onClick={handleReady}
          >
            {isReady ? '✗ HAZIR DEĞİLİM' : '✓ HAZIR'}
          </button>

          {amHost && (
            <button
              className="btn btn-orange btn-lg"
              style={{ width: '100%' }}
              onClick={handleStart}
              disabled={!allReady || starting}
            >
              {starting ? 'Başlatılıyor...' : allReady ? '▶ OYUNU BAŞLAT' : 'Tüm oyuncular hazır olsun'}
            </button>
          )}

          <button className="btn btn-ghost" style={{ width: '100%' }} onClick={onLeave}>
            ← ANA MENÜ
          </button>
        </div>
      </div>
    </div>
  );
};

export default Lobby;
